import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Typography } from '@mui/material';
import { last, sortBy } from 'lodash';

import { ScheduleTaskStatusSubText_TaskFragment, TaskStatus } from 'src/generated/mloop-graphql';

import useDurationLabel from 'src/components/DurationLabel/useDurationLabel';
import { FCWithFragments } from 'src/components/types';

type ScheduleTaskStatusSubTextFragmentProps = {
  // eslint-disable-next-line modernloop/component-with-fragments.cjs
  task: ScheduleTaskStatusSubText_TaskFragment;
};

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const ScheduleTaskStatusSubText: FCWithFragments<ScheduleTaskStatusSubTextFragmentProps> = ({ task }) => {
  const { t } = useTranslation();
  /**
   * This is a temporary work around to get the latest data.
   * BE is going to add a comparator and send the sorted array soon.
   */
  const taskAvailabilityRequest = task?.availabilityRequests
    ? last(
        sortBy(task.availabilityRequests, (availabilityRequest) => {
          return new Date(availabilityRequest.createdAt);
        })
      )
    : null;

  /**
   * This is a temporary work around to get the latest data.
   * BE is going to add a comparator and send the sorted array soon.
   */
  const taskSelfScheduleRequest = task.selfScheduleRequests
    ? last(
        sortBy(task.selfScheduleRequests, (selfScheduleRequest) => {
          return new Date(selfScheduleRequest.createdAt);
        })
      )
    : null;

  const durationLabel = useDurationLabel(taskAvailabilityRequest?.createdAt || taskSelfScheduleRequest?.createdAt);

  const label = useMemo(() => {
    switch (task.status) {
      case TaskStatus.WaitingForCandidate: {
        if (taskAvailabilityRequest?.id) {
          return t('hiring-dashboard-tasks:tasks.details.availabilityRequestCreated', { durationLabel });
        }
        if (taskSelfScheduleRequest?.id) {
          return t('hiring-dashboard-tasks:tasks.details.selfScheduleRequestCreated', { durationLabel });
        }
        return null;
      }
      case TaskStatus.Scheduled:
      case TaskStatus.NeedsAvailability:
      case TaskStatus.ReadyToSchedule:
      case TaskStatus.Hold:
      case TaskStatus.ReadyToSendToCandidate:
      case TaskStatus.Completed:
      case TaskStatus.Canceled:
      default:
        return null;
    }
  }, [durationLabel, task.status, taskAvailabilityRequest?.id, taskSelfScheduleRequest?.id, t]);

  if (!label) {
    return null;
  }

  return <Typography variant="body2">{label}</Typography>;
};

ScheduleTaskStatusSubText.fragments = {
  task: gql`
    fragment ScheduleTaskStatusSubText_task on Task {
      id
      status
      availabilityRequests {
        id
        createdAt
      }
      selfScheduleRequests {
        id
        createdAt
        creatorEmployeeId
      }
    }
  `,
};

export default ScheduleTaskStatusSubText;
